import type { WireScope, WireScopeEntry } from '@/shared/offline';

import {
    childScope,
    isMultiCountKey,
    multiCountKey,
    parseQuestionKey,
    questionKey,
    ROOT_SCOPE,
    runtimeNodeId,
    type AnswerKey,
    type MultiScope,
    type NodeId,
    type QuestionId,
    type RuntimeNode,
    type TemplateNodeId,
} from '../engine';

/**
 * De enige vertaling tussen de sleutels van de opname-engine en het
 * draadformaat van `/sync/*`.
 *
 * | engine                  | draad                                                     |
 * | ----------------------- | --------------------------------------------------------- |
 * | `"412"`                 | `question_id: 412, multi_scope: []`                       |
 * | `"412@178.1"`           | `question_id: 412, multi_scope: [{178, 1}]`               |
 * | `"412@156.0/178.1"`     | `question_id: 412, multi_scope: [{156, 0}, {178, 1}]`     |
 * | `"m@178"`               | `container_node_id: 178, multi_scope: []`                 |
 * | `"m@178#156.0"`         | `container_node_id: 178, multi_scope: [{156, 0}]`         |
 * | knoop `"310#300.1"`     | `node_id: 310, multi_scope: [{300, 1}]`                   |
 * | instantie `"300#300.1"` | `node_id: 300, multi_scope: [{300, 1}]`                   |
 *
 * Een `ask_once`-vraag krijgt van de engine altijd de wortelsleutel, en dus
 * altijd een lege `multi_scope` - precies wat het contract vraagt.
 *
 * Bouwen gaat via de functies van `engine/keys.ts`; na het ontleden wordt de
 * sleutel opnieuw opgebouwd en vergeleken, zodat een wijziging in het
 * sleutelformaat hier meteen faalt in plaats van stil verkeerde antwoorden op
 * te leveren.
 */

export class WireKeyError extends Error {
    constructor(key: string) {
        super(`Sleutel "${key}" is niet naar het draadformaat te vertalen.`);
        this.name = 'WireKeyError';
    }
}

/** Engine-bereik → `multi_scope`, van buiten naar binnen. */
export function scopeToWire(scope: MultiScope): WireScopeEntry[] {
    if (scope === ROOT_SCOPE) {
        return [];
    }

    const entries = scope.split('/').map((segment) => {
        const [container, index] = segment.split('.');
        const entry = { container_node_id: Number(container), index: Number(index) };

        if (
            !Number.isInteger(entry.container_node_id) ||
            !Number.isInteger(entry.index) ||
            entry.index < 0
        ) {
            throw new WireKeyError(scope);
        }

        return entry;
    });

    if (scopeFromWire(entries) !== scope) {
        throw new WireKeyError(scope);
    }

    return entries;
}

/** `multi_scope` → engine-bereik. */
export function scopeFromWire(scope: WireScope): MultiScope {
    return scope.reduce<MultiScope>(
        (parent, entry) => childScope(parent, entry.container_node_id, entry.index),
        ROOT_SCOPE,
    );
}

export interface WireAnswerKey {
    questionId: QuestionId;
    multiScope: WireScopeEntry[];
}

export function answerKeyToWire(key: AnswerKey): WireAnswerKey {
    if (isMultiCountKey(key)) {
        throw new WireKeyError(key);
    }

    const { questionId, scope } = parseQuestionKey(key);

    if (!Number.isInteger(questionId)) {
        throw new WireKeyError(key);
    }

    const multiScope = scopeToWire(scope);

    if (answerKeyFromWire(questionId, multiScope) !== key) {
        throw new WireKeyError(key);
    }

    return { questionId, multiScope };
}

export function answerKeyFromWire(questionId: QuestionId, scope: WireScope): AnswerKey {
    return questionKey(questionId, scopeFromWire(scope));
}

export interface WireMultiCountKey {
    containerNodeId: TemplateNodeId;
    multiScope: WireScopeEntry[];
}

/** De aantalsvraag van een container (`"m@178#156.0"`) → container plus het bereik waarin hij staat. */
export function multiCountKeyToWire(key: AnswerKey): WireMultiCountKey {
    if (!isMultiCountKey(key)) {
        throw new WireKeyError(key);
    }

    const nodeId: NodeId = key.slice(key.indexOf('@') + 1);
    const separator = nodeId.indexOf('#');
    const containerNodeId = Number(separator < 0 ? nodeId : nodeId.slice(0, separator));
    const scope = separator < 0 ? ROOT_SCOPE : nodeId.slice(separator + 1);

    if (!Number.isInteger(containerNodeId)) {
        throw new WireKeyError(key);
    }

    const multiScope = scopeToWire(scope);

    if (multiCountKeyFromWire(containerNodeId, multiScope) !== key) {
        throw new WireKeyError(key);
    }

    return { containerNodeId, multiScope };
}

export function multiCountKeyFromWire(
    containerNodeId: TemplateNodeId,
    scope: WireScope,
): AnswerKey {
    return multiCountKey(runtimeNodeId(containerNodeId, scopeFromWire(scope)));
}

export interface WireNodeAddress {
    nodeId: TemplateNodeId;
    multiScope: WireScopeEntry[];
}

/**
 * Een knoop van de runtime-boom → zijn adres op de draad: de templateknoop en het
 * bereik waarin hij staat. Een instantie ("Berging 2") is haar container, met de
 * eigen regel van die container als laatste in `multi_scope` - zo noemt de engine
 * haar ook (`"300#300.1"`).
 */
export function nodeAddressToWire(
    node: Pick<RuntimeNode, 'templateNodeId' | 'scope'>,
): WireNodeAddress {
    return { nodeId: node.templateNodeId, multiScope: scopeToWire(node.scope) };
}

/** Het adres op de draad → de id van de knoop in de runtime-boom. */
export function nodeIdFromWire(nodeId: TemplateNodeId, scope: WireScope): NodeId {
    return runtimeNodeId(nodeId, scopeFromWire(scope));
}
